import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, DollarSign, Users, Shield, FileCheck, Clock, Target, Award } from 'lucide-react';
import SEO from '@/components/SEO';

const WhyIdnty = () => {
  const differentiators = [
    {
      icon: Zap,
      title: 'Rapid Deployment',
      desc: "Most consultancies take 4–6 months. We deliver production-ready identity infrastructure in 6–8 weeks. Our proven methodology means we've done this dozens of times — your team won't be learning on the job.",
    },
    {
      icon: DollarSign,
      title: 'Transparent, Fixed Pricing',
      desc: 'No vague "depends on scope" quotes or surprise invoices. You know the exact cost upfront, with clear deliverables and timelines.',
    },
    {
      icon: Users,
      title: 'Ongoing Partnership',
      desc: 'Our commitment extends beyond deployment. We provide continuous governance, regular access reviews, and dedicated audit support to ensure your identity infrastructure remains secure and compliant.',
    },
    {
      icon: Shield,
      title: 'Holistic Security Posture',
      desc: 'We don\'t just implement IAM — we ensure your external security signals (SSL, email authentication, domain reputation) align with your internal controls. Your prospects verify these before they even talk to sales.',
    },
    {
      icon: FileCheck,
      title: 'Compliance-Ready Architecture',
      desc: 'Every implementation maps to SOC 2 CC6 controls from day one — not as an expensive retrofit when certification becomes urgent.',
    },
  ];

  const comparison = [
    { feature: 'Implementation Time', traditional: '4–6 months', idnty: '6–8 weeks' },
    { feature: 'Pricing Model', traditional: 'Time & materials', idnty: 'Fixed cost' },
    { feature: 'Post-Deployment', traditional: 'Documentation handoff', idnty: 'Ongoing governance' },
    { feature: 'Compliance Mapping', traditional: 'Retrofit approach', idnty: 'Built-in from day one' },
    { feature: 'External Security', traditional: 'Out of scope', idnty: 'Included' },
  ];

  return (
    <div>
      <SEO
        title="Why Choose IDNTY - Fast, Fixed-Price IAM Solutions"
        description="Discover why companies choose IDNTY for identity management. Rapid 6-8 week deployment, transparent fixed pricing, ongoing partnership, and compliance-ready architecture."
        keywords="why IDNTY, IAM consultancy, identity management services, fast IAM deployment, fixed price IAM, compliance ready IAM"
        ogTitle="Why Choose IDNTY - Fast, Fixed-Price IAM Solutions"
        ogDescription="Discover why companies choose IDNTY for identity management. Rapid 6-8 week deployment, transparent fixed pricing, ongoing partnership, and compliance-ready architecture."
        canonicalUrl="https://idnty.one/why-idnty"
      />
      {/* Hero */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--gold)', fontSize: '0.9rem', letterSpacing: '2px', marginBottom: '1rem' }}>
            WHY IDNTY
          </p>
          <h1 className="hero-title" style={{ marginBottom: '1.5rem' }}>
            UNIFIED IDENTITY WITHOUT THE USUAL HEADACHES
          </h1>
          <p style={{ fontSize: '1.15rem', maxWidth: '640px', margin: '0 auto', lineHeight: '1.7', color: 'var(--gray)' }}>
            Traditional IAM projects drag on for months, exceed budgets, and leave you with documentation instead of results. We do things differently.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '2rem' }}>
            HOW WE COMPARE
          </h2>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0', border: '2px solid var(--charcoal)', background: 'var(--white)' }}>
              {/* Header Row */}
              <div style={{ background: 'var(--charcoal)', color: 'var(--white)', padding: '1.25rem 1rem', fontWeight: '700', textAlign: 'center', fontSize: '0.85rem', letterSpacing: '1px' }}>
                ASPECT
              </div>
              <div style={{ background: 'var(--charcoal)', color: 'var(--white)', padding: '1.25rem 1rem', fontWeight: '700', textAlign: 'center', fontSize: '0.85rem', letterSpacing: '1px' }}>
                TRADITIONAL
              </div>
              <div style={{ background: 'var(--gold)', color: 'var(--black)', padding: '1.25rem 1rem', fontWeight: '700', textAlign: 'center', fontSize: '0.85rem', letterSpacing: '1px' }}>
                IDNTY-1
              </div>
              {/* Data Rows */}
              {comparison.map((row, i) => (
                <React.Fragment key={i}>
                  <div style={{ background: i % 2 === 0 ? 'var(--white)' : 'var(--light-gray)', padding: '1.25rem 1rem', fontWeight: '600', borderBottom: '1px solid var(--gray)', display: 'flex', alignItems: 'center' }}>
                    {row.feature}
                  </div>
                  <div style={{ background: i % 2 === 0 ? 'var(--white)' : 'var(--light-gray)', padding: '1.25rem 1rem', textAlign: 'center', color: 'var(--gray)', borderBottom: '1px solid var(--gray)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {row.traditional}
                  </div>
                  <div style={{ background: i % 2 === 0 ? 'var(--white)' : 'var(--light-gray)', padding: '1.25rem 1rem', textAlign: 'center', fontWeight: '600', color: 'var(--gold)', borderBottom: '1px solid var(--gray)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {row.idnty}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '2rem' }}>
            WHAT SETS US APART
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
            {differentiators.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="card-heavy" style={{ background: 'var(--light-gray)', display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                  <Icon size={40} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <div>
                    <h3 className="feature-heading" style={{ marginBottom: '0.5rem' }}>{item.title}</h3>
                    <p style={{ margin: 0, lineHeight: '1.7', fontSize: '1rem', color: 'var(--gray)' }}>{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding" style={{ background: 'var(--charcoal)', color: 'var(--white)' }}>
        <div className="container">
          <div className="grid-3" style={{ textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: '3rem', fontWeight: '800', color: 'var(--gold)', marginBottom: '0.5rem' }}>6-8</div>
              <div style={{ fontSize: '1rem', letterSpacing: '1px' }}>WEEKS TO DEPLOY</div>
            </div>
            <div>
              <div style={{ fontSize: '3rem', fontWeight: '800', color: 'var(--gold)', marginBottom: '0.5rem' }}>100%</div>
              <div style={{ fontSize: '1rem', letterSpacing: '1px' }}>FIXED PRICING</div>
            </div>
            <div>
              <div style={{ fontSize: '3rem', fontWeight: '800', color: 'var(--gold)', marginBottom: '0.5rem' }}>24/7</div>
              <div style={{ fontSize: '1rem', letterSpacing: '1px' }}>ONGOING SUPPORT</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--black)', color: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="section-title" style={{ color: 'var(--white)', marginBottom: '1rem' }}>
            READY TO UNIFY YOUR IDENTITY?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem', color: 'var(--gray)' }}>
            Let's discuss how IDNTY can transform your identity infrastructure.
          </p>
          <Link to="/contact" className="btn btn-gold">
            Schedule Assessment
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyIdnty;
